import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDeletePricingPlan } from 'src/features/pricingPlan/api/useDeletePricingPlan';
import { LIST_PRICING_PLANS_RESOURCE } from 'src/features/pricingPlan/api/useListPricingPlans';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PricingPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingPlan: PricingPlan;
    children?: React.ReactNode;
    onDeleted?: () => void;
}

function DeletePricingPlanFormModal<TData>({
    selectedPricingPlan,
    semantics,
    children,
    onDeleted,
}: PricingPlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deletePricingPlan, isPending: isPendingDelete } = useDeletePricingPlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLANS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.deletePricingPlanSuccess),
                description: formatMessage(pricingPlanMessages.deletePricingPlanSuccessDescription, {
                    entry: selectedPricingPlan.translations.find((translation) => translation.language === locale)
                        ?.name,
                }),
            });
            onDeleted?.();
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deletePricingPlan({ planId: selectedPricingPlan.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={pricingPlanMessages.deletePricingPlanSubtitle.id}
                                    defaultMessage={pricingPlanMessages.deletePricingPlanSubtitle.defaultMessage}
                                    values={{
                                        entry: selectedPricingPlan.translations.find(
                                            (translation) => translation.language === locale,
                                        )?.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={pricingPlanMessages.confirmDeletePricingPlan.id}
                            defaultMessage={pricingPlanMessages.confirmDeletePricingPlan.defaultMessage}
                            values={{
                                entry: selectedPricingPlan.translations.find(
                                    (translation) => translation.language === locale,
                                )?.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeletePricingPlanFormModal;
